import { useEffect, useMemo, useState } from 'react'
import {Search, Edit2, Trash2, Power, Loader2, GraduationCap, X, Save, Eye} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import {
  CURSOS_AROMATERAPIA_LISTA,
  rotuloCursoInscrito,
} from '@/data/cursosAromaterapia'

// ── Constantes compartilhadas ──────────────────────────────────────────────
export const ORIGENS_INSCRITO = [
  'Capacitacao (negocio)',
  'Capacitacao Equipe',
  'Curso',
  'Curso Aromaterapia',
] as const

const ROTULO_ORIGEM: Record<string, string> = {
  'Capacitacao (negocio)': 'Seleção para Negócio e Capacitação',
  'Capacitacao Equipe': 'Capacitação da Equipe',
  Curso: 'Uma Gota de Equilíbrio',
  'Curso Aromaterapia': 'Cursos de Aromaterapia',
}

const OPCOES_CURSO = [
  { value: 'uma-gota', label: 'Uma Gota de Equilíbrio' },
  ...CURSOS_AROMATERAPIA_LISTA.map((c) => ({ value: c.id, label: c.titulo })),
  { value: 'selecao', label: 'Seleção para Negócio e Capacitação' },
  { value: 'equipe', label: 'Capacitação da Equipe' },
]

export const STATUS_INSCRITO = [
  'Inscrita',
  'Link enviado',
  'Curso em andamento',
  'Curso concluido',
  'Certificado emitido',
] as const

const ROTULO_STATUS: Record<string, string> = {
  Inscrita: 'Inscrita',
  'Link enviado': 'Link enviado',
  'Curso em andamento': 'Curso em andamento',
  'Curso concluido': 'Curso concluído',
  'Certificado emitido': 'Certificado emitido',
}

const COR_STATUS: Record<string, string> = {
  Inscrita: 'bg-[#EDE0D4] text-[#5F5E5A]',
  'Link enviado': 'bg-[#D8F3DC] text-[#2D6A4F]',
  'Curso em andamento': 'bg-[#FEF3C7] text-[#92400E]',
  'Curso concluido': 'bg-[#DBEAFE] text-[#1E40AF]',
  'Certificado emitido': 'bg-[#2D6A4F] text-white',
}

export interface Inscrito {
  _id: string
  nome?: string
  usuario?: string
  email?: string
  whatsapp?: string
  origem?: string
  indicadoPor?: string
  cursoId?: string
  dataInscricao?: string
  statusInscricao?: string
  distribuidora?: string
  assinatura?: string
  ativoInscrito?: boolean
  // /curso
  motivoInscricao?: string
  interesseRenda?: string
  // /capacitacao (selecao)
  status?: string
  respostaCompartilhar?: string
  respostaConheceOleos?: string
  respostaCadastroDoterra?: string
  declaracaoAceita?: boolean
}

interface AbaInscritosProps {
  // Quando definido, filtra apenas inscritos indicados por essa consultora
  // (usado no painel da consultora). Quando undefined/null, mostra todos (admin).
  filtrarPorConsultora?: string | null
  // true = admin (mostra coluna "Indicado por", filtro por consultora/distribuidora, ações completas)
  isAdmin?: boolean
}

function formatarData(iso?: string) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return '—'
  }
}

export default function AbaInscritos({
  filtrarPorConsultora = null,
  isAdmin = false,
}: AbaInscritosProps) {
  const [inscritos, setInscritos] = useState<Inscrito[]>([])
  const [loading, setLoading] = useState(true)
  const [busca, setBusca] = useState('')
  const [filtroOrigem, setFiltroOrigem] = useState('')
  const [filtroCurso, setFiltroCurso] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [filtroConsultora, setFiltroConsultora] = useState('')
  const [filtroDistribuidora, setFiltroDistribuidora] = useState('')
  const [dataInicial, setDataInicial] = useState('')
  const [dataFinal, setDataFinal] = useState('')

  const [editando, setEditando] = useState<Inscrito | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [visualizando, setVisualizando] = useState<Inscrito | null>(null)

  const carregar = async () => {
    setLoading(true)
    try {
      const resultado = await lumi.entities.capacitacao.list({
        sort: { dataInscricao: -1 },
      })
      const lista = (resultado?.list || []) as Inscrito[]
      setInscritos(lista)
    } catch (e) {
      console.error('[inscritos] erro ao carregar', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    carregar()
  }, [])

  // Lista de consultoras (valores de indicadoPor) para o filtro do admin
  const consultorasDisponiveis = useMemo(() => {
    const set = new Set<string>()
    inscritos.forEach((i) => {
      if (i.indicadoPor && i.indicadoPor.trim()) set.add(i.indicadoPor.trim())
    })
    return Array.from(set).sort()
  }, [inscritos])

  const distribuidorasDisponiveis = useMemo(() => {
    const set = new Set<string>()
    inscritos.forEach((i) => {
      if (i.distribuidora && i.distribuidora.trim()) set.add(i.distribuidora.trim())
    })
    return Array.from(set).sort()
  }, [inscritos])

  const filtrados = useMemo(() => {
    return inscritos.filter((i) => {
      // Escopo por consultora (painel da consultora)
      if (filtrarPorConsultora) {
        if ((i.indicadoPor || '').toLowerCase() !== filtrarPorConsultora.toLowerCase()) {
          return false
        }
      }

      if (filtroOrigem && (i.origem || '') !== filtroOrigem) return false
      if (filtroCurso) {
        const cursoId = i.cursoId || ''
        if (filtroCurso === 'uma-gota') {
          if (cursoId !== 'uma-gota' && i.origem !== 'Curso') return false
          if (i.origem === 'Curso Aromaterapia') return false
        } else if (filtroCurso === 'selecao') {
          if (i.origem !== 'Capacitacao (negocio)') return false
        } else if (filtroCurso === 'equipe') {
          if (i.origem !== 'Capacitacao Equipe') return false
        } else if (cursoId !== filtroCurso) {
          return false
        }
      }
      if (filtroStatus && (i.statusInscricao || 'Inscrita') !== filtroStatus) return false
      if (filtroConsultora && (i.indicadoPor || '') !== filtroConsultora) return false
      if (filtroDistribuidora && (i.distribuidora || '') !== filtroDistribuidora) return false

      if (dataInicial) {
        if (!i.dataInscricao || new Date(i.dataInscricao) < new Date(dataInicial)) return false
      }
      if (dataFinal) {
        const fim = new Date(dataFinal)
        fim.setHours(23, 59, 59, 999)
        if (!i.dataInscricao || new Date(i.dataInscricao) > fim) return false
      }

      if (busca.trim()) {
        const q = busca.trim().toLowerCase()
        const alvo = `${i.nome || ''} ${i.usuario || ''} ${i.email || ''} ${i.whatsapp || ''}`.toLowerCase()
        if (!alvo.includes(q)) return false
      }
      return true
    })
  }, [
    inscritos,
    filtrarPorConsultora,
    filtroOrigem,
    filtroCurso,
    filtroStatus,
    filtroConsultora,
    filtroDistribuidora,
    dataInicial,
    dataFinal,
    busca,
  ])

  // ── Ações ────────────────────────────────────────────────────────────────
  const atualizarStatus = async (inscrito: Inscrito, novoStatus: string) => {
    try {
      await lumi.entities.capacitacao.update(inscrito._id, {
        statusInscricao: novoStatus,
        updatedAt: new Date().toISOString(),
      })
      setInscritos((prev) =>
        prev.map((x) => (x._id === inscrito._id ? { ...x, statusInscricao: novoStatus } : x)),
      )
    } catch (e) {
      console.error('[inscritos] erro ao atualizar status', e)
      alert('Não foi possível atualizar o status. Tente novamente.')
    }
  }

  const desativarInscrito = async (inscrito: Inscrito) => {
    if (!confirm(`Desativar a inscrição de ${inscrito.nome || 'inscrito'}?`)) return
    try {
      await lumi.entities.capacitacao.update(inscrito._id, {
        ativoInscrito: false,
        updatedAt: new Date().toISOString(),
      })
      setInscritos((prev) =>
        prev.map((x) => (x._id === inscrito._id ? { ...x, ativoInscrito: false } : x)),
      )
    } catch (e) {
      console.error('[inscritos] erro ao desativar', e)
      alert('Não foi possível desativar. Tente novamente.')
    }
  }

  const excluirInscrito = async (inscrito: Inscrito) => {
    if (!confirm(`Excluir definitivamente a inscrição de ${inscrito.nome || 'inscrito'}? Esta ação não pode ser desfeita.`)) return
    try {
      await lumi.entities.capacitacao.delete(inscrito._id)
      setInscritos((prev) => prev.filter((x) => x._id !== inscrito._id))
    } catch (e) {
      console.error('[inscritos] erro ao excluir', e)
      alert('Não foi possível excluir. Tente novamente.')
    }
  }

  const salvarEdicao = async () => {
    if (!editando) return
    setSalvando(true)
    try {
      await lumi.entities.capacitacao.update(editando._id, {
        nome: editando.nome,
        email: editando.email,
        whatsapp: editando.whatsapp,
        origem: editando.origem,
        statusInscricao: editando.statusInscricao,
        distribuidora: editando.distribuidora,
        updatedAt: new Date().toISOString(),
      })
      setInscritos((prev) =>
        prev.map((x) => (x._id === editando._id ? { ...x, ...editando } : x)),
      )
      setEditando(null)
    } catch (e) {
      console.error('[inscritos] erro ao salvar edição', e)
      alert('Não foi possível salvar. Tente novamente.')
    } finally {
      setSalvando(false)
    }
  }

  // Status editável (dropdown inline) — usado por admin e consultora
  const StatusSelect = ({ inscrito }: { inscrito: Inscrito }) => (
    <select
      value={inscrito.statusInscricao || 'Inscrita'}
      onChange={(e) => atualizarStatus(inscrito, e.target.value)}
      className={`text-xs font-medium rounded-full px-3 py-1.5 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#52B788] ${COR_STATUS[inscrito.statusInscricao || 'Inscrita'] || 'bg-[#EDE0D4] text-[#5F5E5A]'}`}
    >
      {STATUS_INSCRITO.map((s) => (
        <option key={s} value={s}>
          {ROTULO_STATUS[s]}
        </option>
      ))}
    </select>
  )

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap className="text-[#2D6A4F]" size={22} />
        <h2 className="font-cormorant text-2xl font-semibold text-[#2C2C2A]">
          Inscritos
        </h2>
      </div>
      <p className="text-sm text-[#5F5E5A] mb-5">
        {filtrarPorConsultora
          ? 'Pessoas que se inscreveram pelos seus links de cursos e capacitação.'
          : 'Inscritos nos cursos de aromaterapia, Uma Gota de Equilíbrio e seleção para negócio.'}
      </p>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-[#EDE0D4] p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B08968]" size={16} />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar nome, e-mail ou WhatsApp"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
            />
          </div>

          <select
            value={filtroCurso}
            onChange={(e) => setFiltroCurso(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
          >
            <option value="">Todos os cursos</option>
            {OPCOES_CURSO.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <select
            value={filtroOrigem}
            onChange={(e) => setFiltroOrigem(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
          >
            <option value="">Todas as origens</option>
            {ORIGENS_INSCRITO.map((o) => (
              <option key={o} value={o}>
                {ROTULO_ORIGEM[o]}
              </option>
            ))}
          </select>

          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
          >
            <option value="">Todos os status</option>
            {STATUS_INSCRITO.map((s) => (
              <option key={s} value={s}>
                {ROTULO_STATUS[s]}
              </option>
            ))}
          </select>

          {isAdmin && (
            <select
              value={filtroConsultora}
              onChange={(e) => setFiltroConsultora(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
            >
              <option value="">Todas as consultoras</option>
              {consultorasDisponiveis.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}

          {isAdmin && distribuidorasDisponiveis.length > 0 && (
            <select
              value={filtroDistribuidora}
              onChange={(e) => setFiltroDistribuidora(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
            >
              <option value="">Todas as distribuidoras</option>
              {distribuidorasDisponiveis.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}

          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dataInicial}
              onChange={(e) => setDataInicial(e.target.value)}
              className="w-full px-2 py-2.5 rounded-xl border border-[#EDE0D4] text-xs focus:outline-none focus:border-[#52B788]"
              title="Data inicial"
            />
            <span className="text-[#B08968] text-xs">até</span>
            <input
              type="date"
              value={dataFinal}
              onChange={(e) => setDataFinal(e.target.value)}
              className="w-full px-2 py-2.5 rounded-xl border border-[#EDE0D4] text-xs focus:outline-none focus:border-[#52B788]"
              title="Data final"
            />
          </div>
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-[#5F5E5A]">
          <Loader2 className="animate-spin mr-2" size={20} />
          Carregando inscritos...
        </div>
      ) : filtrados.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#EDE0D4] py-16 text-center text-[#5F5E5A]">
          Nenhum inscrito encontrado com os filtros atuais.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#EDE0D4] overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-[#EDE0D4] bg-[#F8F4EF]">
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">E-mail / WhatsApp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Curso</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Origem</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Indicado por</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Por que quer aprender?</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Interesse em renda extra?</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Inscrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Status</th>
                {isAdmin && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-texto-escuro">Ações</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtrados.map((i) => (
                <tr
                  key={i._id}
                  className={`border-b border-[#EDE0D4] last:border-0 ${i.ativoInscrito === false ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-3 text-sm text-[#2C2C2A] font-medium">{i.nome || '—'}</td>
                  <td className="px-4 py-3 text-xs text-[#5F5E5A]">
                    <div>{i.email || '—'}</div>
                    <div className="text-[#B08968]">{i.whatsapp || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#2C2C2A]">
                    {rotuloCursoInscrito(i)}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#2C2C2A]">
                    {ROTULO_ORIGEM[i.origem || ''] || i.origem || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#2C2C2A]">{i.indicadoPor || '—'}</td>
                  <td className="px-4 py-3 text-xs text-[#5F5E5A] max-w-[180px]">
                    {(i as any).motivoInscricao ? (
                      <span title={(i as any).motivoInscricao}>
                        {(i as any).motivoInscricao.length > 45
                          ? (i as any).motivoInscricao.slice(0, 45) + '…'
                          : (i as any).motivoInscricao}
                      </span>
                    ) : <span className="text-[#B08968]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5F5E5A] max-w-[180px]">
                    {(i as any).interesseRenda ? (
                      <span title={(i as any).interesseRenda}>
                        {(i as any).interesseRenda.length > 45
                          ? (i as any).interesseRenda.slice(0, 45) + '…'
                          : (i as any).interesseRenda}
                      </span>
                    ) : <span className="text-[#B08968]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5F5E5A]">{formatarData(i.dataInscricao)}</td>
                  <td className="px-4 py-3">
                    <StatusSelect inscrito={i} />
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setVisualizando({ ...i })}
                          className="p-1.5 rounded-lg text-[#2D6A4F] hover:bg-[#D8F3DC] transition-colors"
                          title="Ver respostas"
                        >
                          <Eye size={15} />
                        </button>
                        <button
                          onClick={() => setEditando({ ...i })}
                          className="p-1.5 rounded-lg text-[#5F5E5A] hover:bg-[#F8F4EF] transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => desativarInscrito(i)}
                          className="p-1.5 rounded-lg text-[#B08968] hover:bg-[#EDE0D4] transition-colors"
                          title="Desativar"
                        >
                          <Power size={15} />
                        </button>
                        <button
                          onClick={() => excluirInscrito(i)}
                          className="p-1.5 rounded-lg text-[#EF4444] hover:bg-[#FEE2E2] transition-colors"
                          title="Excluir"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de visualização de respostas */}
      {visualizando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-cormorant text-xl font-semibold text-[#2C2C2A]">
                Respostas de {visualizando.nome || 'inscrita'}
              </h3>
              <button onClick={() => setVisualizando(null)} className="text-[#5F5E5A] hover:text-[#2C2C2A]">
                <X size={20} />
              </button>
            </div>

            {/* Origem */}
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="inline-block px-3 py-1 rounded-full bg-[#D8F3DC] text-[#2D6A4F] text-xs font-medium">
                {ROTULO_ORIGEM[(visualizando.origem || '')] || visualizando.origem || 'Origem desconhecida'}
              </span>
              <span className="inline-block px-3 py-1 rounded-full bg-[#F8F4EF] border border-[#EDE0D4] text-[#2C2C2A] text-xs font-medium">
                {rotuloCursoInscrito(visualizando)}
              </span>
            </div>

            {/* Capacitacao (negocio) — dados da seleção */}
            {(visualizando.origem === 'Capacitacao (negocio)' || !visualizando.origem) && (
              <div className="space-y-4">
                <PerguntaResposta
                  pergunta="Status atual"
                  resposta={visualizando.status}
                />
                <PerguntaResposta
                  pergunta="Você gosta de compartilhar conhecimentos, gerar transformações na vida das pessoas e se vê numa postura de aprender como se conectar com as pessoas de forma simples e estratégica?"
                  resposta={visualizando.respostaCompartilhar}
                />
                <PerguntaResposta
                  pergunta="Você conhece os óleos essenciais?"
                  resposta={visualizando.respostaConheceOleos}
                />
                <PerguntaResposta
                  pergunta="Você já fez algum tipo de cadastro no site da dōTERRA?"
                  resposta={visualizando.respostaCadastroDoterra}
                />
                <div className="bg-[#F8F4EF] rounded-xl border border-[#EDE0D4] px-4 py-3">
                  <p className="text-xs font-medium text-[#B08968] mb-1">Declaração aceita</p>
                  <p className="text-sm text-[#2C2C2A]">
                    {visualizando.declaracaoAceita ? 'Sim — declarou informações verdadeiras' : 'Não registrado'}
                  </p>
                </div>
              </div>
            )}

            {/* Curso — 2 perguntas */}
            {visualizando.origem === 'Curso' && (
              <div className="space-y-4">
                <PerguntaResposta
                  pergunta="Curso"
                  resposta={rotuloCursoInscrito(visualizando)}
                />
                <PerguntaResposta
                  pergunta="Por que você quer aprender mais sobre óleos essenciais?"
                  resposta={(visualizando as any).motivoInscricao}
                />
                <PerguntaResposta
                  pergunta="No futuro, você teria interesse em compartilhar os óleos essenciais e gerar uma renda extra com isso?"
                  resposta={(visualizando as any).interesseRenda}
                />
              </div>
            )}

            {/* Cursos de Aromaterapia */}
            {visualizando.origem === 'Curso Aromaterapia' && (
              <div className="space-y-4">
                <PerguntaResposta
                  pergunta="Curso"
                  resposta={rotuloCursoInscrito(visualizando)}
                />
                <div className="bg-[#F8F4EF] rounded-xl border border-[#EDE0D4] px-4 py-4 text-sm text-[#5F5E5A]">
                  Inscrição direta no curso, sem perguntas adicionais. Acesso liberado ao player e ao material da aula.
                </div>
              </div>
            )}

            {/* Capacitacao Equipe — sem perguntas */}
            {visualizando.origem === 'Capacitacao Equipe' && (
              <div className="bg-[#F8F4EF] rounded-xl border border-[#EDE0D4] px-4 py-4 text-sm text-[#5F5E5A]">
                Inscrita via Capacitação Equipe. Sem perguntas adicionais.
              </div>
            )}

            <button
              onClick={() => setVisualizando(null)}
              className="mt-6 w-full px-4 py-2.5 rounded-full border border-[#EDE0D4] text-sm text-[#5F5E5A] hover:bg-[#F8F4EF] transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Modal de edição (admin) */}
      {editando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-cormorant text-xl font-semibold text-[#2C2C2A]">Editar inscrito</h3>
              <button onClick={() => setEditando(null)} className="text-[#5F5E5A] hover:text-[#2C2C2A]">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#2C2C2A] mb-1">Nome</label>
                <input
                  type="text"
                  value={editando.nome || ''}
                  onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#2C2C2A] mb-1">E-mail</label>
                <input
                  type="email"
                  value={editando.email || ''}
                  onChange={(e) => setEditando({ ...editando, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#2C2C2A] mb-1">WhatsApp</label>
                <input
                  type="text"
                  value={editando.whatsapp || ''}
                  onChange={(e) => setEditando({ ...editando, whatsapp: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#2C2C2A] mb-1">Origem</label>
                <select
                  value={editando.origem || ''}
                  onChange={(e) => setEditando({ ...editando, origem: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
                >
                  {ORIGENS_INSCRITO.map((o) => (
                    <option key={o} value={o}>
                      {ROTULO_ORIGEM[o]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#2C2C2A] mb-1">Status</label>
                <select
                  value={editando.statusInscricao || 'Inscrita'}
                  onChange={(e) => setEditando({ ...editando, statusInscricao: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#EDE0D4] text-sm focus:outline-none focus:border-[#52B788]"
                >
                  {STATUS_INSCRITO.map((s) => (
                    <option key={s} value={s}>
                      {ROTULO_STATUS[s]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setEditando(null)}
                className="flex-1 px-4 py-2.5 rounded-full border border-[#EDE0D4] text-sm text-[#5F5E5A] hover:bg-[#F8F4EF] transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={salvarEdicao}
                disabled={salvando}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#2D6A4F] text-white text-sm font-medium hover:bg-[#1f4f3a] transition-colors disabled:opacity-60"
              >
                {salvando ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function PerguntaResposta({ pergunta, resposta }: { pergunta: string; resposta?: string }) {
  return (
    <div className="bg-[#F8F4EF] rounded-xl border border-[#EDE0D4] px-4 py-3">
      <p className="text-xs font-medium text-[#B08968] mb-1">{pergunta}</p>
      {resposta ? (
        <p className="text-sm text-[#2C2C2A]">{resposta}</p>
      ) : (
        <p className="text-sm text-[#B08968] italic">Sem resposta registrada</p>
      )}
    </div>
  )
}
